import {createContext, memo, useCallback, useContext, useMemo, useRef, useState} from 'react'

type ViewOptionsMenuControls = {
  toggle: () => void
  open: () => void
  close: () => void
}

type ViewOptionsMenuRefContextType = {
  anchorRef: React.MutableRefObject<HTMLButtonElement | null>
  isMenuOpen: boolean
  registerMenuControls: (controls: ViewOptionsMenuControls | null) => void
  toggleMenu: () => void
  openMenu: () => void
  closeMenu: () => void
  setIsMenuOpen: React.Dispatch<React.SetStateAction<boolean>>
}

export const ViewOptionsMenuRefContext = createContext<ViewOptionsMenuRefContextType | null>(null)

export const ViewOptionsMenuRefContextProvider = memo(function ViewOptionsMenuRefContextProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const anchorRef = useRef<HTMLButtonElement | null>(null)
  const controlsRef = useRef<ViewOptionsMenuControls | null>(null)
  const [isMenuOpen, setIsMenuOpen] = useState(false)

  const registerMenuControls = useCallback((controls: ViewOptionsMenuControls | null) => {
    controlsRef.current = controls
  }, [])

  const toggleMenu = useCallback(() => {
    controlsRef.current?.toggle()
  }, [])

  const openMenu = useCallback(() => {
    controlsRef.current?.open()
  }, [])

  const closeMenu = useCallback(() => {
    controlsRef.current?.close()
  }, [])

  return (
    <ViewOptionsMenuRefContext.Provider
      value={useMemo<ViewOptionsMenuRefContextType>(
        () => ({
          anchorRef,
          isMenuOpen,
          registerMenuControls,
          toggleMenu,
          openMenu,
          closeMenu,
          setIsMenuOpen,
        }),
        [closeMenu, isMenuOpen, openMenu, registerMenuControls, toggleMenu],
      )}
    >
      {children}
    </ViewOptionsMenuRefContext.Provider>
  )
})

export const useViewOptionsMenuRef = () => {
  const context = useContext(ViewOptionsMenuRefContext)
  if (!context) {
    throw new Error('useViewOptionsMenu must be used within a ViewOptionsMenuRefContextProvider')
  }

  return context
}

try{ ViewOptionsMenuRefContext.displayName ||= 'ViewOptionsMenuRefContext' } catch {}
try{ ViewOptionsMenuRefContextProvider.displayName ||= 'ViewOptionsMenuRefContextProvider' } catch {}